import React, { useEffect, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { Redirect, useHistory, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import ButtonDivider from "@common/LifeAtButton/ButtonDivider";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { auth, firebase } from "@firebase-local/firebase";
import CameraOnIcon from "@icons/VideoCamera.svg";
import Styles from "@themes/Styles";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { RouteUtil } from "@util/index";
import { useRoomController } from "../../../../features/room/RoomController";
import * as RoomService from "../../../../features/room/RoomService";
import { RoomSettingsContextProvider } from "../../../../features/room/RoomSettingsContext";
import { RoomParams } from "../../../../features/room/RoomTypes";
import RoomPresenceControls from "../../../../features/room/components/RoomVideoChat/RoomPresenceControls";

const RoomSelectorRow = styled.span`
    display: flex;
    align-items: center;
`;

// NOTE: this whole component is very fragile due to the routing logic.
//  It must be tested thoroughly if being changed, and ideally should be rewritten ASAP.
export default function RoomsButtons() {
    const { roomId }: RoomParams = useParams();

    const history = useHistory();

    const { user, setUser, userData }: UserContextType = useUserContext();

    // If we are ready to render something from this component (after user data is found or not)
    const [ready, setReady] = useState(false);

    useEffect(() => {
        // Auth state change listener
        const unsubscribe = auth.onAuthStateChanged(function (stateChangeUser: firebase.User | null) {
            if (stateChangeUser) {
                setUser(stateChangeUser);
                setReady(true);
            } else {
                setUser(null);
                setReady(true);
            }
        });
        // Unsubscribe from the listener when the component is unmounted
        return () => {
            unsubscribe();
        };
    }, []);

    const [currRoomId, setCurrRoomId] = useState<string | undefined>(
        roomId || userData?.roomsData?.personalRoomId || undefined,
    );

    const [attemptRoomDataInit, setAttemptRoomDataInit] = useState(userData?.roomsData !== undefined ?? false);
    // The base usage of the application involves a user being in their "Room", so we
    // init that data for the user if it hasn't been already and set the state variable
    // for their personal room ID when the user object is loaded.
    useEffect(() => {
        if (user && userData) {
            if (userData.roomsData === undefined && !attemptRoomDataInit) {
                setAttemptRoomDataInit(true);
                RoomService.initUserRoomsData();
            } else {
                if (!currRoomId) {
                    setCurrRoomId(userData?.roomsData?.personalRoomId);
                    setShowRoomButtons(true);
                }
            }
        }
    }, [user, userData]);

    const [showRoomButtons, setShowRoomButtons] = useState<boolean>(
        RouteUtil.queryParamPresent(QueryParameters.EnterRoom) ?? false,
    );
    useEffect(() => {
        if (userData && currRoomId) {
            setIsHost(isRoomOwnedByUser(currRoomId, userData));
        }
    }, [userData, currRoomId]);

    const [isHost, setIsHost] = useState<boolean | undefined>(undefined);
    useEffect(() => {
        if (isHost) {
            setShowRoomButtons(true);
        }
    }, [isHost]);

    /* Handle how different routes affect app behavior.
     * - If there was a room ID given that is not the user's personal room ID, the user
     *      should enter that room (provided they have appropriate permissions).
     * - If the user has their personal room ID configured, we should show that as
     *      the current route.
     * - If a space ID was added as a query parameter, that info is added as well
     *      (and also in spacePlayerController.ts)
     */
    useEffect(() => {
        if (isHost !== undefined) {
            if (roomId !== undefined || userData?.roomsData?.personalRoomId) {
                const personalRoom = userData?.roomsData?.personalRoomId;

                if (roomId !== undefined && roomId !== personalRoom) {
                    // If the Room ID was found, we only put the user in the Room if they
                    // are the host/owner.
                    if (isHost || RouteUtil.queryParamPresent(QueryParameters.EnterRoom)) {
                        setShowRoomButtons(true);

                        if (!isHost && !RouteUtil.queryParamPresent(QueryParameters.EnterRoom)) {
                            history.push({
                                pathname: `/${Routes.RoomAccess}/${roomId}`,
                                search: RouteUtil.getQueryParamsStr(),
                            });
                        } else if (isHost) {
                            history.replace({
                                pathname: `/${Routes.Room}/${roomId}`,
                                search: RouteUtil.getQueryParamsStr(),
                            });
                        }
                    } else {
                        setShowRoomButtons(false);
                    }
                } else if ((roomId === undefined && personalRoom) || roomId === personalRoom) {
                    // No Room ID was found in the URL, but the user has their Room initialized so
                    // we "redirect" them to their Room.
                    setShowRoomButtons(true);

                    if (roomId !== personalRoom) {
                        history.replace({
                            pathname: `/${Routes.Room}/${personalRoom}`,
                            search: RouteUtil.getQueryParamsStr(),
                        });
                    }
                }
            }
        }
    }, [roomId, isHost, userData?.roomsData]);

    const roomController = useRoomController();

    const roomControls = currRoomId && <RoomPresenceControls roomId={currRoomId} roomController={roomController} />;

    const roomButtonsOverlay = (
        <RoomSettingsContextProvider roomId={roomId}>
            <RoomSelectorRow>{roomControls}</RoomSelectorRow>
        </RoomSettingsContextProvider>
    );

    // NOTE: all of the below routing handling works -- DO NOT TOUCH

    // Depending on user's auth status and the room's status, we either display
    // the room buttons (i.e. the user is in their room), or redirect them to the Room access page.
    let roomComponentToRender;
    if (isHost !== undefined && showRoomButtons !== undefined) {
        roomComponentToRender =
            showRoomButtons || isHost ? (
                roomButtonsOverlay
            ) : (
                <Redirect to={`/${Routes.RoomAccess}/${roomId}?${RouteUtil.getQueryParams()}`} />
            );
    }

    if (user === null && (isHost !== undefined || showRoomButtons !== undefined)) {
        roomComponentToRender = <Redirect to={`/${Routes.RoomAccess}/${roomId}?${RouteUtil.getQueryParams()}`} />;
    }
    if (user === null && isHost === undefined && showRoomButtons === undefined) {
        roomComponentToRender = <Redirect to={`/${Routes.RoomAccess}/${roomId}?${RouteUtil.getQueryParams()}`} />;
    }

    // If no roomId was found at all, then don't render anything.
    return ready && currRoomId && roomComponentToRender !== undefined ? roomComponentToRender : <DummyRoomButtons />;
}

const DummyButtonsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px;
    min-height: ${Styles.overlayButtonHeight};
    height: ${Styles.overlayButtonHeight};
    max-height: ${Styles.overlayButtonHeight};
`;

const CameraIcon = styled(CameraOnIcon)`
    fill: ${(props) => props.theme.textColorPrimary};
    transform: scale(1.3);
`;

const ControlBarButtonDivider = styled(ButtonDivider)`
    height: 80%;
    margin: unset;
`;

function DummyRoomButtons() {
    const { setSignInView }: CallToActionContextType = useCallToActionContext();

    const handleShowSignInView = () => {
        if (isMobile || isTablet) {
            return;
        } else {
            setSignInView(SignUpCTASource.ROOMS);
        }
    };

    const dummyInviteButton = (
        <span data-tip={"Create an account to video chat with your friends 🤩"} data-for="rooms-invite-btn-unauth-tip">
            <LifeAtButtonV2 onClick={handleShowSignInView} size={"small"}>
                Invite
            </LifeAtButtonV2>
            <ReactTooltip id={"rooms-invite-btn-unauth-tip"} place={"bottom"} />
        </span>
    );

    const dummyStartCallButton = (
        <LifeAtButtonV2 onClick={handleShowSignInView} size={"small"}>
            <CameraIcon />
        </LifeAtButtonV2>
    );

    return (
        <RoomSelectorRow>
            <DummyButtonsWrapper>
                {dummyStartCallButton}
                <ControlBarButtonDivider />
                {dummyInviteButton}
            </DummyButtonsWrapper>
        </RoomSelectorRow>
    );
}
